import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";
import { put } from "@rails/request.js";

// Connects to data-controller="candidate-job-sortable"
export default class extends Controller {
  connect() {
    this.modalWrap = null;

    this.sortable = Sortable.create(this.element, {
      scroll: true,
      onStart: this.onStart.bind(this),
      onEnd: this.onEnd.bind(this),
      onMove: this.onMove.bind(this),
      animation: 150,
      filter: ".disabled, [data-draggable='false'], [data-draggable='false'] *",
      group: "candidate-job",
      ghostClass: "opacity-50",
      scrollSensitivity: 100, // px, how near the mouse must be to an edge to start scrolling.
      scrollSpeed: 10, // px, speed of the scrolling
      bubbleScroll: true,
    });
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy();
      this.sortable = null;
    }
  }

  onStart() {
    this.cleanupTooltips();
  }

  cleanupTooltips() {
    document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
      try {
        const instance = window.bootstrap?.Tooltip?.getInstance(el);
        if (instance) {
          instance.hide();
        }
      } catch (e) {}
    });
    // Remove lingering visible tooltip popups created by popper
    document.querySelectorAll('.tooltip.show').forEach((el) => el.remove());
  }

  onMove(event) {
    if (event.dragged?.dataset.draggable === "false") return false;
    if (this.isMatchmakingColumn(event.from) || this.isMatchmakingColumn(event.to)) return false;
    if (this.element.dataset.organization !== "company") return true;

    if (this.isEsproApprovedStep(event.from) && event.to !== event.from) return false;

    if (this.element.dataset.marketplace !== "true") return true;

    const fromKind = event.from.dataset.stepKind;
    const fromName = (event.from.dataset.stepName || "").toLowerCase();
    const destinationKind = event.to.dataset.stepKind;
    const destinationName = (event.to.dataset.stepName || "").toLowerCase();

    const isEarlyStep = (kind, name) =>
      ["profile_registration", "application", "mapping"].includes(kind) ||
      ["cadastro", "candidatura", "mapeamento"].includes(name);

    const isValidationStep = (kind, name) =>
      kind === "validation" || name === "validacao";

    const isRecommendationStep = (kind, name) =>
      kind === "recommended" || name.startsWith("recomend") || name.startsWith("recommend");

    // From early steps (cadastro/candidatura), candidates can only move forward to validation (or stay/move within early steps)
    if (isEarlyStep(fromKind, fromName) && !isEarlyStep(destinationKind, destinationName) && !isValidationStep(destinationKind, destinationName)) {
      return false;
    }

    // From validation step, candidates can only move backward to early steps (or stay in validation). Forward moves must use actions.
    if (isValidationStep(fromKind, fromName) && !isEarlyStep(destinationKind, destinationName) && !isValidationStep(destinationKind, destinationName)) {
      return false;
    }

    const beforeShortlist = isEarlyStep(destinationKind, destinationName) || isValidationStep(destinationKind, destinationName) || isRecommendationStep(destinationKind, destinationName);

    if (event.dragged.dataset.beenShortlist === "true" && beforeShortlist) return false;
    if (event.dragged.dataset.hunterCandidate === "true" && isEarlyStep(destinationKind, destinationName)) return false;
    return true;
  }

  async onEnd(event) {
    const { from, to, oldIndex, newIndex, item } = event;

    if (item.dataset.draggable === "false") {
      this.revertElement(from, item, oldIndex);
      return;
    }

    if (from === to && oldIndex === newIndex) return;

    if (this.isMatchmakingColumn(from) || this.isMatchmakingColumn(to)) {
      this.revertElement(from, item, oldIndex);
      return;
    }

    if (this.isEsproApprovedStep(from) && from !== to) {
      this.revertElement(from, item, oldIndex);
      return;
    }

    if (this.isEsproApprovedStep(to) && from !== to) {
      const stepTitle = to.dataset.stepTitle || to.dataset.stepRawName || "Aprovado";
      const confirmed = await this.confirmEsproApproval(stepTitle);
      if (!confirmed) {
        this.revertElement(from, item, oldIndex);
        return;
      }
    }

    this.updateCardStyle(item, to.dataset.stepKind);

    const responseData = await this.updateStep(
      newIndex,
      item,
      to.dataset.stepId,
      to.dataset.stepKind
    );

    if (responseData?.error) {
      alert(responseData.error);
      this.revertElement(from, item, oldIndex);
      return;
    }

    if (responseData?.status) {
      alert(this.backgroundCheckTexts(responseData?.status));

      !["VALID_DOCUMENT", "DOCUMENT_CHECK_PENDING"].includes(
        responseData.status
      ) && this.revertElement(from, item, oldIndex);
      return;
    }

    if (this.isEsproApprovedStep(to)) {
      item.dataset.draggable = "false";
    }
  }

  async updateStep(newIndex, item, stepId, stepKind) {
    const jobId = item.dataset.jobId;
    const candidateJobId = item.dataset.candidateJobId;
    const resource = item.dataset.resource || "jobs";
    const organization = this.element.dataset.organization || "hunter";

    const filterParams = this.currentFilterSearchParams();
    if (stepKind === "background_check") {
      filterParams.set("background_check", "true");
    }
    const queryString = filterParams.toString();
    const urlParams = queryString ? `?${queryString}` : "";

    const response = await put(
      `/ats/${organization}/${resource}/${jobId}/candidate_jobs/${candidateJobId}/update_step${urlParams}`,
      {
        responseKind: "turbo-stream",
        body: JSON.stringify({
          step_id: stepId,
          position: newIndex,
        }),
      }
    );

    if (!response.ok) {
      try {
        return await response.json;
      } catch (error) {
        return { error: "Não foi possível mover o candidato." };
      }
    }

    try {
      return await response.json;
    } catch (error) {
      return response;
    }
  }

  revertElement(parent, item, index) {
    const children = parent.children;

    if (children.length === 0 || index >= children.length) {
      parent.appendChild(item);
    } else {
      parent.insertBefore(item, children[index]);
    }

    this.removeBackgroundCheckStyles(item);
  }

  updateCardStyle(item, stepKind) {
    if (stepKind === "background_check") {
      item.querySelectorAll(".candidate-pipeline-card").forEach((el) => {
        el.classList.add("background-check-pending");

        const badgesContainer = el.querySelector("#candidate_job-card-badges");
        if (
          !badgesContainer ||
          badgesContainer.querySelector(".exato-digital-badge")
        ) {
          return;
        }

        const span = document.createElement("span");
        span.className = "badge text-warning exato-digital-badge";
        span.style.border = "1px solid #ff6b00";
        span.style.setProperty("border", "1px solid #ff6b00", "important");
        span.innerText = this.backgroundCheckTexts("PENDING");

        badgesContainer.appendChild(span);
      });
    } else {
      this.removeBackgroundCheckStyles(item);
    }
  }

  removeBackgroundCheckStyles(item) {
    item.querySelectorAll(".candidate-pipeline-card").forEach((el) => {
      el.classList.remove(
        "background-check-pending",
        "background-check-fulfilled"
      );

      const badgesContainer = el.querySelector("#candidate_job-card-badges");

      if (badgesContainer) {
        const badge = badgesContainer.querySelector(".exato-digital-badge");
        if (badge) {
          badge.remove();
        }
      }
    });
  }

  backgroundCheckTexts(status) {
    const texts = {
      BLANK_DOCUMENT: {
        "pt-BR": "Candidato não possui documento registrado",
        en: "Candidate has no registered document",
      },
      INVALID_DOCUMENT: {
        "pt-BR": "O documento do candidato é inválido",
        en: "The candidate's document is invalid",
      },
      DOCUMENT_CHECK_FULFILLED: {
        "pt-BR": "A verificação de documento já foi realizada",
        en: "Document verification has already been completed",
      },
      DOCUMENT_CHECK_PENDING: {
        "pt-BR": "A verificação de documento está em andamento",
        en: "Document verification has been pending",
      },
      VALID_DOCUMENT: {
        "pt-BR": "Documento válido, verificação em andamento",
        en: "Valid document, verification in progress",
      },
      CREDITS_ERROR: {
        "pt-BR": "Não há créditos suficientes para realizar a verificação",
        en: "There are not enough credits to perform the verification",
      },
      ERROR: {
        "pt-BR": "Ocorreu um erro ao realizar a verificação",
        en: "An error occurred while performing the verification",
      },
      PENDING: {
        "pt-BR": "Pendente",
        en: "Pending",
      },
    };

    return texts[status]?.[this.element.dataset.appLocale] || "";
  }

  isEsproApprovedStep(stepEl) {
    if (!stepEl) return false;
    if (this.element.dataset.organization !== "company") return false;

    const isEspro = stepEl.dataset.espro === "true" || this.element.dataset.espro === "true";
    if (!isEspro) return false;

    const kind = stepEl.dataset.stepKind;
    const name = (stepEl.dataset.stepName || "").toLowerCase();
    const title = (stepEl.dataset.stepTitle || "").trim().toLowerCase();
    const rawName = (stepEl.dataset.stepRawName || "").trim().toLowerCase();

    return kind === "hired" || name === "aprovado" || title === "aprovado" || rawName === "aprovado";
  }

  isMatchmakingColumn(stepEl) {
    if (!stepEl) return false;
    const kind = stepEl.dataset.stepKind;
    const col = stepEl.dataset.column;
    const name = (stepEl.dataset.stepName || stepEl.dataset.stepRawName || "").toLowerCase();

    return kind === "matchmaking" || col === "matchmaking" || name.includes("matchmaking");
  }

  confirmEsproApproval(stepTitle = "Aprovado") {
    return new Promise((resolve) => {
      let modalEl = document.getElementById("espro-confirm-approval-modal");
      if (!modalEl) {
        modalEl = document.createElement("div");
        modalEl.id = "espro-confirm-approval-modal";
        modalEl.className = "modal fade";
        modalEl.tabIndex = -1;
        modalEl.setAttribute("aria-hidden", "true");
        document.body.appendChild(modalEl);
      }

      const isPt = (this.element.dataset.appLocale || "pt-BR").startsWith("pt");
      const title = this.element.dataset.confirmApprovalTitle || (isPt ? "Confirmar aprovação" : "Confirm approval");
      const templateMessage = this.element.dataset.confirmApprovalMessage ||
        (isPt ? "Tem certeza que deseja mover este candidato para <strong>%{step}</strong>?" : "Are you sure you want to move this candidate to <strong>%{step}</strong>?");
      const message = templateMessage.replace("%{step}", stepTitle);
      const warningText = this.element.dataset.confirmApprovalWarning ||
        (isPt
          ? "Atenção: esta ação é definitiva. Uma vez aprovado, o candidato não poderá ser movido para outras etapas."
          : "Attention: this action is definitive. Once approved, the candidate cannot be moved to other stages.");
      const cancelText = this.element.dataset.confirmApprovalCancel || (isPt ? "Cancelar" : "Cancel");
      const confirmText = this.element.dataset.confirmApprovalConfirm || (isPt ? "Confirmar" : "Confirm");

      modalEl.innerHTML = `
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content rounded-3 shadow">
            <div class="modal-header border-bottom-0 pb-0">
              <h5 class="modal-title fw-bold text-dark">${title}</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="${cancelText}"></button>
            </div>
            <div class="modal-body py-3">
              <p class="mb-2 text-dark">${message}</p>
              <div class="alert alert-warning d-flex align-items-center gap-2 mb-0">
                <i class="fa-solid fa-triangle-exclamation fa-lg"></i>
                <div class="small">
                  ${warningText}
                </div>
              </div>
            </div>
            <div class="modal-footer border-top-0 pt-0">
              <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">${cancelText}</button>
              <button type="button" class="btn btn-primary" id="espro-confirm-approval-btn">${confirmText}</button>
            </div>
          </div>
        </div>
      `;

      const ModalClass = window.bootstrap?.Modal || (typeof bootstrap !== "undefined" && bootstrap.Modal);
      if (!ModalClass) {
        const confirmed = window.confirm(`${message.replace(/<[^>]+>/g, "")}\n\n${warningText}`);
        resolve(confirmed);
        return;
      }

      let bsModal = ModalClass.getInstance(modalEl);
      if (bsModal) {
        bsModal.dispose();
      }
      bsModal = new ModalClass(modalEl, { backdrop: "static", keyboard: true });
      const confirmBtn = modalEl.querySelector("#espro-confirm-approval-btn");

      let resolved = false;

      const onConfirm = () => {
        resolved = true;
        cleanup();
        bsModal.hide();
        resolve(true);
      };

      const onHidden = () => {
        cleanup();
        if (!resolved) {
          resolve(false);
        }
      };

      const cleanup = () => {
        confirmBtn?.removeEventListener("click", onConfirm);
        modalEl.removeEventListener("hidden.bs.modal", onHidden);
      };

      confirmBtn?.addEventListener("click", onConfirm);
      modalEl.addEventListener("hidden.bs.modal", onHidden);

      bsModal.show();
    });
  }

  currentFilterSearchParams() {
    const params = new URLSearchParams();
    const filterContainer = document.getElementById("candidate-jobs-persisted-params");

    if (filterContainer) {
      filterContainer.querySelectorAll("input:not([form])").forEach((input) => {
        if (input.name && input.value !== undefined && input.value !== "") {
          params.append(input.name, input.value);
        }
      });
      if (Array.from(params.keys()).length > 0) {
        return params;
      }
    }

    if (window.location.search) {
      return new URLSearchParams(window.location.search);
    }

    return params;
  }
}
